import React, { useState, useEffect, useContext } from 'react';
import {
    Wallet, Save, Lock, X,
    AlertCircle, CheckCircle2, Loader2, IndianRupee, Users
} from 'lucide-react';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const AdvanceAndFinalize = ({ selectedMonth, payouts, onRefresh }) => {
    const { user } = useContext(AuthContext);
    const [showAdvanceModal, setShowAdvanceModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Auth context states pulled from session storage/state management
    const [currentUser, setCurrentUser] = useState({
        id: user?.id || '',
        role: user?.role || 'Staff' // e.g., Owner, Admin, Staff
    });

    // List of wallet holding users for Owner/Admin selection dropdown
    const [walletUsers, setWalletUsers] = useState([]);

    const [advanceForm, setAdvanceForm] = useState({
        employee_id: '',
        wallet_user_id: '', // Tracks whose wallet the cash is debited from
        amount: '',
        notes: '',
        date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        if (showAdvanceModal) {
            // Set default wallet user if user is staff
            if (currentUser.role !== 'Owner' && currentUser.role !== 'Admin' && currentUser.role !== 'owner' && currentUser.role !== 'admin') {
                setAdvanceForm(prev => ({ ...prev, wallet_user_id: currentUser.id }));
            } else {
                fetchWalletHoldingUsers();
            }
        }
    }, [showAdvanceModal]);

    const fetchWalletHoldingUsers = async () => {
        try {
            // Reuses your wallet intelligence endpoint to populate supervisor list
            const res = await api.get('get_wallet_intelligence.php');
            setWalletUsers(res.data.supervisors || []);
        } catch (err) {
            console.error("Failed to load user wallets list", err);
        }
    };

    // 1. Record a New Advance
    const handleRecordAdvance = async (e) => {
        e.preventDefault();
        if (!advanceForm.wallet_user_id) {
            alert("Please assign a source wallet account for this cash disbursal.");
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('record_employee_advance.php', advanceForm);
            if (res.data.status === 'success') {
                setShowAdvanceModal(false);
                setAdvanceForm({
                    employee_id: '',
                    wallet_user_id: '',
                    amount: '',
                    notes: '',
                    date: new Date().toISOString().split('T')[0]
                });
                onRefresh();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to record advance");
        } finally {
            setLoading(false);
        }
    };

    // 2. Finalize Month (Lock Balances for Next Month)
    const handleFinalizeMonth = async () => {
        if (!window.confirm(`Are you sure you want to finalize ${selectedMonth}? This will carry forward all balances to the next month.`)) return;

        setLoading(true);
        try {
            const res = await api.post('finalize_monthly_ledger.php', {
                month_year: selectedMonth,
                payouts: payouts
            });
            if (res.data.status === 'success') {
                alert("Month Finalized! Balances moved to next month.");
                onRefresh();
            }
        } catch (err) { alert("Finalization failed"); }
        finally { setLoading(false); }
    };

    return (
        <div className="flex gap-4 mb-8">
            <button
                onClick={() => setShowAdvanceModal(true)}
                className="flex-1 bg-white border-2 border-slate-900 text-slate-900 py-4 rounded-3xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-slate-50 transition-all"
            >
                <Wallet size={18} /> Give Advance (Cash Out)
            </button>

            <button
                onClick={handleFinalizeMonth}
                disabled={loading}
                className="flex-1 bg-slate-900 text-white py-4 rounded-3xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-emerald-600 transition-all shadow-xl"
            >
                {loading ? <Loader2 className="animate-spin" /> : <Lock size={18} />} Finalize {selectedMonth}
            </button>

            {/* --- ADVANCE MODAL --- */}
            {showAdvanceModal && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
                    <form onSubmit={handleRecordAdvance} className="bg-white w-full max-w-md rounded-[48px] shadow-2xl p-12 space-y-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-2xl font-black italic tracking-tighter uppercase text-slate-900">Issue Advance</h2>
                                <p className="text-[9px] font-black tracking-widest text-indigo-500 uppercase mt-0.5">Debited Payroll Disbursal</p>
                            </div>
                            <button type="button" onClick={() => setShowAdvanceModal(false)} className="p-2 bg-slate-100 rounded-full text-slate-400 hover:bg-slate-900 hover:text-white transition-all"><X size={20} /></button>
                        </div>

                        <div className="space-y-4">
                            {/* TARGET EMPLOYEE SELECTOR */}
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Recipient Employee</label>
                                <select
                                    required
                                    className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-sm outline-none border-2 border-transparent focus:border-indigo-500 transition-all"
                                    value={advanceForm.employee_id}
                                    onChange={e => setAdvanceForm({ ...advanceForm, employee_id: e.target.value })}
                                >
                                    <option value="">Select Employee...</option>
                                    {payouts.map(p => <option key={p.employee_id} value={p.employee_id}>{p.employee_name}</option>)}
                                </select>
                            </div>

                            {/* CONDITIONAL WALLET SELECTOR DROPDOWN */}
                            {(currentUser.role === 'Owner' || currentUser.role === 'Admin' || currentUser.role === 'owner' || currentUser.role === 'admin') ? (
                                <div>
                                    <label className="text-[9px] font-black text-indigo-600 uppercase tracking-widest block mb-2 ml-1 flex items-center gap-1">
                                        <Users size={12} /> Funding Source Cash Wallet
                                    </label>
                                    <select
                                        required
                                        className="w-full p-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-wider outline-none border-2 border-transparent focus:border-indigo-400 transition-all"
                                        value={advanceForm.wallet_user_id}
                                        onChange={e => setAdvanceForm({ ...advanceForm, wallet_user_id: e.target.value })}
                                    >
                                        <option value="" className="text-slate-400">Select Wallet Account...</option>
                                        {walletUsers.map(user => (
                                            <option key={user.id} value={user.id} className="text-white bg-slate-900">
                                                {user.name} (Bal: ₹{parseFloat(user.balance).toFixed(0)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center gap-2">
                                    <AlertCircle size={16} className="text-indigo-600 shrink-0" />
                                    <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-tight">
                                        Funds will be securely debited from your personal staff cash wallet.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Advance Quantum Amount (₹)</label>
                                <div className="relative">
                                    <IndianRupee className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        required
                                        type="number"
                                        placeholder="Amount"
                                        className="w-full p-5 pl-12 bg-slate-50 rounded-3xl font-black text-xl outline-none"
                                        value={advanceForm.amount}
                                        onChange={e => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Payment Effective Date</label>
                                <input
                                    required
                                    type="date"
                                    className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none"
                                    value={advanceForm.date}
                                    onChange={e => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Reference Reason Manifest</label>
                                <textarea
                                    placeholder="Reason (e.g. For daughter's school fees)"
                                    className="w-full p-5 bg-slate-50 rounded-3xl font-medium text-sm h-20 outline-none resize-none"
                                    value={advanceForm.notes}
                                    onChange={e => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-5 bg-slate-900 disabled:bg-slate-300 text-white rounded-[32px] font-black text-lg shadow-xl hover:bg-indigo-600 transition-all pt-5 uppercase tracking-wider"
                            >
                                {loading ? "Authorizing Ledger..." : "Confirm Cash Payment"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdvanceAndFinalize;